import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Shield, Lock, Eye, Download, Trash2, AlertTriangle, 
  CheckCircle, Server, Globe, Key, UserCheck, Smartphone, Monitor, Laptop, LogOut,
  Mail, Bell
} from 'lucide-react';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Security = ({ user, setUser }) => {
  const [deletionStatus, setDeletionStatus] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [revokingSession, setRevokingSession] = useState(null);
  const [revokingAll, setRevokingAll] = useState(false);
  
  // Email preferences state
  const [emailPrefs, setEmailPrefs] = useState({
    feature_announcements: true,
    weekly_digest: true,
    social_notifications: true,
    blog_updates: true,
    grazing_recommendations: true
  });
  const [loadingEmailPrefs, setLoadingEmailPrefs] = useState(true);
  const [savingEmailPrefs, setSavingEmailPrefs] = useState(false);

  useEffect(() => {
    checkDeletionStatus();
    loadSessions();
    loadEmailPreferences();
  }, []);

  const loadSessions = async () => {
    try {
      const response = await axios.get(`${API}/auth/sessions`, {
        withCredentials: true
      });
      setSessions(response.data.sessions || []);
    } catch (error) {
      console.error('Error loading sessions:', error);
    } finally {
      setLoadingSessions(false);
    }
  };

  const loadEmailPreferences = async () => {
    try {
      const response = await axios.get(`${API}/email/preferences`, {
        withCredentials: true
      });
      setEmailPrefs({
        feature_announcements: response.data.feature_announcements ?? true,
        weekly_digest: response.data.weekly_digest ?? true,
        social_notifications: response.data.social_notifications ?? true,
        blog_updates: response.data.blog_updates ?? true,
        grazing_recommendations: response.data.grazing_recommendations ?? true
      });
    } catch (error) {
      console.error('Error loading email preferences:', error);
    } finally {
      setLoadingEmailPrefs(false);
    }
  };

  const handleEmailPrefToggle = async (prefKey) => {
    const newValue = !emailPrefs[prefKey];
    const updatedPrefs = { ...emailPrefs, [prefKey]: newValue };
    setEmailPrefs(updatedPrefs);
    
    setSavingEmailPrefs(true);
    try {
      await axios.put(`${API}/email/preferences`, updatedPrefs, {
        withCredentials: true
      });
      toast.success('Email preference updated');
    } catch (error) {
      // Revert on error
      setEmailPrefs(emailPrefs);
      toast.error('Failed to update preference');
    } finally {
      setSavingEmailPrefs(false);
    }
  };

  const handleUnsubscribeAll = async () => {
    if (!window.confirm('Are you sure you want to unsubscribe from all emails?')) {
      return;
    }
    
    setSavingEmailPrefs(true);
    try {
      await axios.post(`${API}/email/unsubscribe-all`, {}, {
        withCredentials: true
      });
      setEmailPrefs({
        feature_announcements: false,
        weekly_digest: false,
        social_notifications: false,
        blog_updates: false,
        grazing_recommendations: false
      });
      toast.success('Unsubscribed from all emails');
    } catch (error) {
      toast.error('Failed to unsubscribe');
    } finally {
      setSavingEmailPrefs(false);
    }
  };

  const revokeSession = async (sessionId) => {
    setRevokingSession(sessionId);
    try {
      await axios.delete(`${API}/auth/sessions/${sessionId}`, {
        withCredentials: true
      });
      toast.success('Session revoked');
      loadSessions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke session');
    } finally {
      setRevokingSession(null);
    }
  };

  const revokeAllOtherSessions = async () => {
    setRevokingAll(true);
    try {
      const response = await axios.post(`${API}/auth/sessions/revoke-all-others`, {}, {
        withCredentials: true
      });
      toast.success(response.data.message);
      loadSessions();
    } catch (error) {
      toast.error('Failed to revoke sessions');
    } finally {
      setRevokingAll(false);
    }
  };

  const getDeviceIcon = (deviceType) => {
    const type = deviceType?.toLowerCase() || '';
    if (type.includes('iphone') || type.includes('mobile') || type.includes('android')) {
      return <Smartphone size={20} />;
    } else if (type.includes('tablet') || type.includes('ipad')) {
      return <Laptop size={20} />;
    }
    return <Monitor size={20} />;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const checkDeletionStatus = async () => {
    try {
      const response = await axios.get(`${API}/security/deletion-status`, {
        withCredentials: true
      });
      setDeletionStatus(response.data);
    } catch (error) {
      console.error('Error checking deletion status:', error);
    }
  };

  const handleExportData = async () => {
    setIsExporting(true);
    try {
      const response = await axios.get(`${API}/security/export-data`, {
        withCredentials: true
      });
      
      // Download as JSON file
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = window.URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `baadtv-data-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Your data has been exported successfully!');
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error('Export limit reached. Please try again later.');
      } else {
        toast.error('Failed to export data');
      }
    } finally {
      setIsExporting(false);
    }
  };

  const handleCancelDeletion = async () => {
    setIsCancelling(true);
    try {
      await axios.post(`${API}/security/cancel-deletion`, {
        confirmation: 'CANCEL'
      }, {
        withCredentials: true
      });
      toast.success('Account deletion cancelled!');
      checkDeletionStatus();
    } catch (error) {
      toast.error('Failed to cancel deletion');
    } finally {
      setIsCancelling(false);
    }
  };

  return (
    <>
      <SEO 
        title="Security & Privacy | baad.tv"
        description="Learn how baad.tv protects your data and manage your privacy settings."
        url="/security"
      />
      <div className="security-container">
        <Navigation user={user} setUser={setUser} currentPage="security" />
        
        <div className="security-content">
          <div className="page-header">
            <h1><Shield className="header-icon" /> Security & Privacy</h1>
            <p>Your data protection and privacy controls</p>
          </div>

          {/* Pending Deletion Warning */}
          {deletionStatus?.pending_deletion && (
            <Card className="warning-card">
              <CardContent className="warning-content">
                <AlertTriangle size={24} />
                <div className="warning-text">
                  <h3>Account Deletion Scheduled</h3>
                  <p>
                    Your account is scheduled for deletion in <strong>{deletionStatus.days_remaining} days</strong>.
                    All your data will be permanently deleted on {new Date(deletionStatus.scheduled_deletion_date).toLocaleDateString()}.
                  </p>
                  <Button 
                    onClick={handleCancelDeletion} 
                    disabled={isCancelling}
                    className="cancel-btn"
                  >
                    {isCancelling ? 'Cancelling...' : 'Cancel Deletion'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* How We Protect Your Data */}
          <section className="security-section">
            <h2><Lock size={20} /> How We Protect Your Data</h2>
            <div className="protection-grid">
              <Card className="protection-card">
                <CardContent>
                  <Globe className="card-icon" />
                  <h3>HTTPS Encryption</h3>
                  <p>All data transmitted between your device and our servers is encrypted using TLS 1.3</p>
                </CardContent>
              </Card>
              <Card className="protection-card">
                <CardContent>
                  <Key className="card-icon" />
                  <h3>Secure Authentication</h3>
                  <p>We use Google OAuth 2.0 for secure sign-in. We never see or store your Google password</p>
                </CardContent>
              </Card>
              <Card className="protection-card">
                <CardContent>
                  <Server className="card-icon" />
                  <h3>Secure Infrastructure</h3>
                  <p>Your data is stored on secure, monitored servers with regular security updates</p>
                </CardContent>
              </Card>
              <Card className="protection-card">
                <CardContent>
                  <UserCheck className="card-icon" />
                  <h3>Session Protection</h3>
                  <p>Sessions expire automatically and are securely stored. Rate limiting prevents abuse</p>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* What Data We Collect */}
          <section className="security-section">
            <h2><Eye size={20} /> What Data We Collect</h2>
            <Card className="data-card">
              <CardContent>
                <div className="data-list">
                  <div className="data-item">
                    <CheckCircle className="check-icon" />
                    <div>
                      <strong>Account Information</strong>
                      <p>Your name, email, and profile picture from Google</p>
                    </div>
                  </div>
                  <div className="data-item">
                    <CheckCircle className="check-icon" />
                    <div>
                      <strong>Shows & Reviews</strong>
                      <p>TV shows you track, ratings, and reviews you write</p>
                    </div>
                  </div>
                  <div className="data-item">
                    <CheckCircle className="check-icon" />
                    <div>
                      <strong>Social Connections</strong>
                      <p>Who you follow and who follows you</p>
                    </div>
                  </div>
                  <div className="data-item">
                    <CheckCircle className="check-icon" />
                    <div>
                      <strong>Activity</strong>
                      <p>Likes, comments, and app usage for improving the experience</p>
                    </div>
                  </div>
                </div>
                <div className="data-note">
                  <p><strong>We do NOT:</strong></p>
                  <ul>
                    <li>Sell your data to third parties</li>
                    <li>Share your viewing habits without consent</li>
                    <li>Store your Google password</li>
                    <li>Track you across other websites</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Active Sessions */}
          <section className="security-section">
            <h2><Monitor size={20} /> Active Sessions</h2>
            <Card className="sessions-card">
              <CardContent>
                <p className="sessions-description">
                  These are the devices currently logged into your account. 
                  You can log out from any session you don't recognize.
                </p>
                
                {loadingSessions ? (
                  <div className="sessions-loading">Loading sessions...</div>
                ) : sessions.length === 0 ? (
                  <div className="no-sessions">No active sessions found</div>
                ) : (
                  <>
                    <div className="sessions-list">
                      {sessions.map((session) => (
                        <div 
                          key={session.id} 
                          className={`session-item ${session.is_current ? 'current' : ''}`}
                        >
                          <div className="session-icon">
                            {getDeviceIcon(session.device_type)}
                          </div>
                          <div className="session-info">
                            <div className="session-device">
                              {session.device_type} · {session.browser}
                              {session.is_current && <span className="current-badge">This device</span>}
                            </div>
                            <div className="session-meta">
                              <span className="session-ip">{session.ip_address}</span>
                              <span className="session-time">Active {formatDate(session.last_active || session.created_at)}</span>
                            </div>
                          </div>
                          {!session.is_current && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => revokeSession(session.id)}
                              disabled={revokingSession === session.id}
                              className="revoke-btn"
                              data-testid={`revoke-${session.id}`}
                            >
                              {revokingSession === session.id ? 'Revoking...' : <LogOut size={16} />}
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    {sessions.filter(s => !s.is_current).length > 0 && (
                      <Button
                        variant="outline"
                        onClick={revokeAllOtherSessions}
                        disabled={revokingAll}
                        className="revoke-all-btn"
                        data-testid="revoke-all-sessions"
                      >
                        {revokingAll ? 'Logging out...' : 'Log out all other devices'}
                      </Button>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </section>

          {/* Your Privacy Controls */}
          <section className="security-section">
            <h2><Shield size={20} /> Your Privacy Controls</h2>
            <div className="controls-grid">
              {/* Email Preferences Card */}
              <Card className="control-card email-prefs-card">
                <CardHeader>
                  <CardTitle><Mail size={20} /> Email Preferences</CardTitle>
                  <CardDescription>
                    Control what emails you receive from baad.tv
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingEmailPrefs ? (
                    <div className="loading-prefs">Loading preferences...</div>
                  ) : (
                    <>
                      <div className="email-prefs-list">
                        <div className="email-pref-item">
                          <div className="pref-info">
                            <Bell size={16} />
                            <div>
                              <strong>Feature Announcements</strong>
                              <span>New features and updates</span>
                            </div>
                          </div>
                          <label className="toggle-switch">
                            <input
                              type="checkbox"
                              checked={emailPrefs.feature_announcements}
                              onChange={() => handleEmailPrefToggle('feature_announcements')}
                              disabled={savingEmailPrefs}
                              data-testid="toggle-feature-announcements"
                            />
                            <span className="toggle-slider"></span>
                          </label>
                        </div>
                        
                        <div className="email-pref-item">
                          <div className="pref-info">
                            <Bell size={16} />
                            <div>
                              <strong>Weekly Digest</strong>
                              <span>Coming episodes for your shows</span>
                            </div>
                          </div>
                          <label className="toggle-switch">
                            <input
                              type="checkbox"
                              checked={emailPrefs.weekly_digest}
                              onChange={() => handleEmailPrefToggle('weekly_digest')}
                              disabled={savingEmailPrefs}
                              data-testid="toggle-weekly-digest"
                            />
                            <span className="toggle-slider"></span>
                          </label>
                        </div>
                        
                        <div className="email-pref-item">
                          <div className="pref-info">
                            <Bell size={16} />
                            <div>
                              <strong>Social Notifications</strong>
                              <span>Comments and follows</span>
                            </div>
                          </div>
                          <label className="toggle-switch">
                            <input
                              type="checkbox"
                              checked={emailPrefs.social_notifications}
                              onChange={() => handleEmailPrefToggle('social_notifications')}
                              disabled={savingEmailPrefs}
                              data-testid="toggle-social-notifications"
                            />
                            <span className="toggle-slider"></span>
                          </label>
                        </div>
                        
                        <div className="email-pref-item">
                          <div className="pref-info">
                            <Bell size={16} />
                            <div>
                              <strong>Blog Updates</strong>
                              <span>New blog posts</span>
                            </div>
                          </div>
                          <label className="toggle-switch">
                            <input
                              type="checkbox"
                              checked={emailPrefs.blog_updates}
                              onChange={() => handleEmailPrefToggle('blog_updates')}
                              disabled={savingEmailPrefs}
                              data-testid="toggle-blog-updates"
                            />
                            <span className="toggle-slider"></span>
                          </label>
                        </div>
                        
                        <div className="email-pref-item">
                          <div className="pref-info">
                            <Bell size={16} />
                            <div>
                              <strong>Monthly Recommendations</strong>
                              <span>AI-powered show suggestions</span>
                            </div>
                          </div>
                          <label className="toggle-switch">
                            <input
                              type="checkbox"
                              checked={emailPrefs.grazing_recommendations}
                              onChange={() => handleEmailPrefToggle('grazing_recommendations')}
                              disabled={savingEmailPrefs}
                              data-testid="toggle-grazing-recommendations"
                            />
                            <span className="toggle-slider"></span>
                          </label>
                        </div>
                      </div>
                      
                      <button 
                        className="unsubscribe-link"
                        onClick={handleUnsubscribeAll}
                        disabled={savingEmailPrefs}
                        data-testid="unsubscribe-all"
                      >
                        Unsubscribe from all emails
                      </button>
                    </>
                  )}
                </CardContent>
              </Card>

              <Card className="control-card">
                <CardHeader>
                  <CardTitle><Download size={20} /> Export Your Data</CardTitle>
                  <CardDescription>
                    Download all your data including shows, reviews, follows, and activity
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button 
                    onClick={handleExportData} 
                    disabled={isExporting}
                    className="export-btn"
                    data-testid="export-data-btn"
                  >
                    {isExporting ? 'Exporting...' : 'Download My Data'}
                  </Button>
                  <p className="control-note">Downloads as .json file · Limited to 3 exports per hour</p>
                </CardContent>
              </Card>

              <Card className="control-card delete-card">
                <CardHeader>
                  <CardTitle><Trash2 size={20} /> Delete Account</CardTitle>
                  <CardDescription>
                    Permanently delete your account and all associated data
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {deletionStatus?.pending_deletion ? (
                    <div className="pending-info">
                      <p className="pending-note">
                        Deletion scheduled in {deletionStatus.days_remaining} days.
                      </p>
                      <Button 
                        onClick={handleCancelDeletion}
                        disabled={isCancelling}
                        variant="outline"
                        size="sm"
                      >
                        {isCancelling ? 'Cancelling...' : 'Cancel Deletion'}
                      </Button>
                    </div>
                  ) : (
                    <>
                      <Button 
                        onClick={() => window.location.href = '/profile'}
                        variant="destructive"
                        className="delete-btn"
                        data-testid="delete-account-btn"
                      >
                        Manage in Profile
                      </Button>
                      <p className="control-note">Choose immediate or 30-day grace period</p>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Contact */}
          <section className="security-section">
            <h2>Questions?</h2>
            <p className="contact-text">
              If you have any questions about our security practices or privacy policy, 
              please reach out through the Help section or contact us directly.
            </p>
          </section>
        </div>

        <style jsx>{`
          .security-container {
            min-height: 100vh;
            background: #121212;
          }

          .security-content {
            max-width: 1000px;
            margin: 0 auto;
            padding: 2rem;
          }

          .page-header {
            margin-bottom: 2rem;
          }

          .page-header h1 {
            font-size: 2.5rem;
            font-weight: 700;
            color: #ffffff;
            display: flex;
            align-items: center;
            gap: 0.75rem;
            margin-bottom: 0.5rem;
          }

          .page-header h1 :global(.header-icon) {
            color: #E62F88;
          }

          .page-header p {
            color: #b0b0b0;
            font-size: 1.1rem;
          }

          .warning-card {
            background: rgba(239, 68, 68, 0.1) !important;
            border: 1px solid #ef4444 !important;
            margin-bottom: 2rem;
          }

          .warning-content {
            display: flex;
            gap: 1rem;
            align-items: flex-start;
            padding: 1.5rem !important;
            color: #ef4444;
          }

          .warning-text h3 {
            color: #ffffff;
            margin-bottom: 0.5rem;
          }

          .warning-text p {
            color: #b0b0b0;
            margin-bottom: 1rem;
          }

          .cancel-btn {
            background: #ef4444 !important;
            color: #ffffff !important;
          }

          .security-section {
            margin-bottom: 3rem;
          }

          .security-section h2 {
            font-size: 1.5rem;
            color: #ffffff;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            margin-bottom: 1.5rem;
          }

          .protection-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
            gap: 1rem;
          }

          .protection-card {
            background: #1a1a1a !important;
            border: 1px solid #333 !important;
          }

          .protection-card :global(.card-icon) {
            color: #E62F88;
            margin-bottom: 1rem;
          }

          .protection-card h3 {
            color: #ffffff;
            font-size: 1rem;
            margin-bottom: 0.5rem;
          }

          .protection-card p {
            color: #b0b0b0;
            font-size: 0.9rem;
            line-height: 1.5;
          }

          .data-card {
            background: #1a1a1a !important;
            border: 1px solid #333 !important;
          }

          .data-list {
            display: flex;
            flex-direction: column;
            gap: 1rem;
            margin-bottom: 1.5rem;
          }

          .data-item {
            display: flex;
            gap: 1rem;
            align-items: flex-start;
          }

          .data-item :global(.check-icon) {
            color: #4ade80;
            flex-shrink: 0;
            margin-top: 2px;
          }

          .data-item strong {
            color: #ffffff;
            display: block;
            margin-bottom: 0.25rem;
          }

          .data-item p {
            color: #b0b0b0;
            font-size: 0.9rem;
            margin: 0;
          }

          .data-note {
            background: #2a2a2a;
            padding: 1rem;
            border-radius: 8px;
          }

          .data-note p {
            color: #ffffff;
            margin-bottom: 0.5rem;
          }

          .data-note ul {
            color: #b0b0b0;
            padding-left: 1.5rem;
            margin: 0;
          }

          .data-note li {
            margin-bottom: 0.25rem;
          }

          /* Sessions Section */
          .sessions-card {
            background: #1a1a1a !important;
            border: 1px solid #333 !important;
          }

          .sessions-description {
            color: #b0b0b0;
            margin-bottom: 1.5rem;
          }

          .sessions-loading, .no-sessions {
            color: #666;
            text-align: center;
            padding: 2rem;
          }

          .sessions-list {
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
            margin-bottom: 1.5rem;
          }

          .session-item {
            display: flex;
            align-items: center;
            gap: 1rem;
            padding: 1rem;
            background: #2a2a2a;
            border-radius: 8px;
            border: 1px solid #333;
          }

          .session-item.current {
            border-color: #E62F88;
            background: rgba(230, 47, 136, 0.1);
          }

          .session-icon {
            color: #b0b0b0;
            flex-shrink: 0;
          }

          .session-item.current .session-icon {
            color: #E62F88;
          }

          .session-info {
            flex: 1;
            min-width: 0;
          }

          .session-device {
            color: #ffffff;
            font-weight: 500;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            flex-wrap: wrap;
          }

          .current-badge {
            background: #E62F88;
            color: #000;
            font-size: 0.7rem;
            padding: 0.15rem 0.5rem;
            border-radius: 10px;
            font-weight: 600;
          }

          .session-meta {
            display: flex;
            gap: 1rem;
            margin-top: 0.25rem;
            font-size: 0.85rem;
            color: #666;
          }

          .revoke-btn {
            color: #ef4444 !important;
            flex-shrink: 0;
          }

          .revoke-btn:hover {
            background: rgba(239, 68, 68, 0.1) !important;
          }

          .revoke-all-btn {
            width: 100%;
            border-color: #ef4444 !important;
            color: #ef4444 !important;
          }

          .revoke-all-btn:hover {
            background: rgba(239, 68, 68, 0.1) !important;
          }

          .controls-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            gap: 1.5rem;
          }

          .control-card {
            background: #1a1a1a !important;
            border: 1px solid #333 !important;
          }

          .control-card :global(.card-title) {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            color: #ffffff;
          }

          .control-card :global(.card-description) {
            color: #b0b0b0;
          }

          .export-btn {
            background: #E62F88 !important;
            color: #000 !important;
            width: 100%;
          }

          .delete-btn {
            width: 100%;
          }

          .control-note {
            color: #666;
            font-size: 0.8rem;
            margin-top: 0.75rem;
            text-align: center;
          }

          .pending-info {
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
          }

          .pending-note {
            color: #f59e0b;
            font-size: 0.9rem;
          }

          .delete-card {
            border-color: rgba(239, 68, 68, 0.3) !important;
          }

          /* Email Preferences Styles */
          .email-prefs-card {
            grid-column: span 2;
          }

          .loading-prefs {
            color: #666;
            text-align: center;
            padding: 1rem;
          }

          .email-prefs-list {
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
          }

          .email-pref-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 1rem;
            background: #2a2a2a;
            border-radius: 8px;
            border: 1px solid #333;
          }

          .pref-info {
            display: flex;
            align-items: center;
            gap: 0.75rem;
          }

          .pref-info svg {
            color: #E62F88;
            flex-shrink: 0;
          }

          .pref-info div {
            display: flex;
            flex-direction: column;
          }

          .pref-info strong {
            color: #ffffff;
            font-size: 0.95rem;
          }

          .pref-info span {
            color: #888;
            font-size: 0.8rem;
          }

          /* Toggle Switch */
          .toggle-switch {
            position: relative;
            display: inline-block;
            width: 48px;
            height: 26px;
            flex-shrink: 0;
          }

          .toggle-switch input {
            opacity: 0;
            width: 0;
            height: 0;
          }

          .toggle-slider {
            position: absolute;
            cursor: pointer;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: #444;
            transition: 0.3s;
            border-radius: 26px;
          }

          .toggle-slider:before {
            position: absolute;
            content: "";
            height: 20px;
            width: 20px;
            left: 3px;
            bottom: 3px;
            background-color: white;
            transition: 0.3s;
            border-radius: 50%;
          }

          .toggle-switch input:checked + .toggle-slider {
            background-color: #E62F88;
          }

          .toggle-switch input:checked + .toggle-slider:before {
            transform: translateX(22px);
          }

          .toggle-switch input:disabled + .toggle-slider {
            opacity: 0.5;
            cursor: not-allowed;
          }

          .unsubscribe-link {
            display: block;
            width: 100%;
            background: none;
            border: none;
            color: #888;
            font-size: 0.85rem;
            text-align: center;
            margin-top: 1rem;
            cursor: pointer;
            padding: 0.5rem;
          }

          .unsubscribe-link:hover {
            color: #ef4444;
            text-decoration: underline;
          }

          .unsubscribe-link:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }

          .contact-text {
            color: #b0b0b0;
            font-size: 1rem;
            line-height: 1.6;
          }

          /* Modal Styles */
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.8);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
            padding: 1rem;
          }

          .delete-modal {
            background: #1a1a1a;
            border-radius: 12px;
            max-width: 500px;
            width: 100%;
            padding: 2rem;
            position: relative;
            border: 1px solid #333;
          }

          .modal-close {
            position: absolute;
            top: 1rem;
            right: 1rem;
            background: none;
            border: none;
            color: #666;
            cursor: pointer;
            padding: 0.5rem;
          }

          .modal-close:hover {
            color: #ffffff;
          }

          .modal-header {
            text-align: center;
            margin-bottom: 1.5rem;
          }

          .modal-header :global(.warning-icon) {
            color: #ef4444;
            margin-bottom: 1rem;
          }

          .modal-header h2 {
            color: #ffffff;
            font-size: 1.5rem;
          }

          .deletion-options {
            display: flex;
            flex-direction: column;
            gap: 1rem;
          }

          .deletion-options p {
            color: #b0b0b0;
            text-align: center;
            margin-bottom: 0.5rem;
          }

          .option-btn {
            display: flex;
            align-items: center;
            gap: 1rem;
            padding: 1.25rem;
            border-radius: 8px;
            border: 1px solid #333;
            background: #2a2a2a;
            cursor: pointer;
            text-align: left;
            transition: all 0.2s;
          }

          .option-btn:hover {
            border-color: #E62F88;
          }

          .option-btn.scheduled {
            color: #f59e0b;
          }

          .option-btn.immediate {
            color: #ef4444;
          }

          .option-btn strong {
            color: #ffffff;
            display: block;
            margin-bottom: 0.25rem;
          }

          .option-btn span {
            color: #b0b0b0;
            font-size: 0.85rem;
          }

          .deletion-confirm {
            text-align: center;
          }

          .deletion-type-badge {
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.5rem 1rem;
            border-radius: 20px;
            font-size: 0.9rem;
            font-weight: 600;
            margin-bottom: 1.5rem;
          }

          .deletion-type-badge.scheduled {
            background: rgba(245, 158, 11, 0.2);
            color: #f59e0b;
          }

          .deletion-type-badge.immediate {
            background: rgba(239, 68, 68, 0.2);
            color: #ef4444;
          }

          .confirm-text {
            color: #b0b0b0;
            margin-bottom: 1.5rem;
            line-height: 1.5;
          }

          .confirm-input {
            text-align: left;
            margin-bottom: 1.5rem;
          }

          .confirm-input label {
            display: block;
            color: #ffffff;
            margin-bottom: 0.5rem;
            font-size: 0.9rem;
          }

          .confirm-input :global(input) {
            background: #2a2a2a;
            border: 1px solid #333;
            color: #ffffff;
          }

          .modal-actions {
            display: flex;
            gap: 1rem;
            justify-content: flex-end;
          }

          @media (max-width: 768px) {
            .security-content {
              padding: 1rem;
              padding-bottom: 100px;
            }

            .page-header h1 {
              font-size: 1.75rem;
            }

            .protection-grid {
              grid-template-columns: 1fr;
            }

            .controls-grid {
              grid-template-columns: 1fr;
            }

            .email-prefs-card {
              grid-column: span 1;
            }

            .delete-modal {
              padding: 1.5rem;
            }

            .modal-actions {
              flex-direction: column-reverse;
            }

            .modal-actions button {
              width: 100%;
            }
          }
        `}</style>
      </div>
    </>
  );
};

export default Security;
